import React, { useState, useEffect, useRef } from "react";
import { FaPaperPlane, FaSpinner, FaCopy, FaRobot, FaUser, FaThumbsUp, FaThumbsDown, FaTimes, FaStop, FaTrash } from 'react-icons/fa';
import "./ChatbotPopup.css";

const createGreetingMessage = (userName) => ({
  id: `greeting-${Date.now()}`,
  text: `Hello${userName ? ` ${userName}` : ''}! I'm **Manual Search Assistant**. I can help you troubleshoot issues, explain probable causes, suggest next steps.\n\nWhat can I help you with today?`,
  sender: 'bot',
  timestamp: new Date(),
  isGreeting: true
});

const ChatbotPopup = ({ onClose, userEmail, userName }) => {
  const storageKey = `ask-ai-chat-${(userEmail || 'anonymous').trim().toLowerCase()}`;
  const [messages, setMessages] = useState([createGreetingMessage(userName)]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(() => `chat_${Date.now()}`);
  const [copiedMessageId, setCopiedMessageId] = useState('');
  const [followUpPromptFor, setFollowUpPromptFor] = useState('');
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  const [feedbackTargetText, setFeedbackTargetText] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [showReasonStep, setShowReasonStep] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [feedbackComments, setFeedbackComments] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const clearFeedbackTimer = () => {
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    try {
      const savedChat = localStorage.getItem(storageKey);
      if (!savedChat) {
        setMessages([createGreetingMessage(userName)]);
        return;
      }

      const parsed = JSON.parse(savedChat);
      const savedMessages = Array.isArray(parsed?.messages) ? parsed.messages : [];
      if (savedMessages.length === 0) {
        setMessages([createGreetingMessage(userName)]);
        return;
      }

      setMessages(savedMessages.map((message) => ({
        ...message,
        timestamp: message.timestamp ? new Date(message.timestamp) : new Date()
      })));
      if (parsed?.conversationId) {
        setConversationId(parsed.conversationId);
      }
    } catch (error) {
      console.error('Error loading ASK AI history:', error);
      setMessages([createGreetingMessage(userName)]);
    }
  }, [storageKey, userName]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        conversationId,
        messages
      }));
    } catch (error) {
      console.error('Error saving ASK AI history:', error);
    }
  }, [conversationId, messages, storageKey]);

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 140)}px`;
  }, [inputText]);

  useEffect(() => () => {
    clearFeedbackTimer();
    abortControllerRef.current?.abort();
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  const buildConversationHistory = () => {
    return messages
      .filter((msg) => !msg.isGreeting)
      .map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));
  };

  const handleCopyMessage = async (messageId, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(''), 1500);
    } catch (error) {
      console.error('Failed to copy AI response:', error);
    }
  };

  const handleStopResponse = () => {
    abortControllerRef.current?.abort();
  };

  const handleCloseChat = () => {
    handleClearChat();
    onClose?.();
  };

  const handleClearChat = () => {
    handleStopResponse();
    const nextConversationId = `chat_${Date.now()}`;
    setConversationId(nextConversationId);
    setMessages([createGreetingMessage(userName)]);
    setInputText('');
    setFollowUpPromptFor('');
    setShowFeedbackPrompt(false);
    setShowReasonStep(false);
    setSelectedReason('');
    setFeedbackComments('');
    setFeedbackTargetText('');
    clearFeedbackTimer();
    localStorage.removeItem(storageKey);
  };

  const NEGATIVE_REASONS = [
    'No response received',
    'Incorrect solution',
    'Incomplete answer',
    'Not relevant to my question',
    'Too long / hard to read',
    'Other'
  ];

  const handleFeedbackSubmit = async (feedbackType, reason = '') => {
    if (!feedbackTargetText || feedbackSubmitting) return;

    setFeedbackSubmitting(true);
    try {
      await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketNumber: 'CHATBOT',
          feedbackType,
          aiResponse: feedbackTargetText,
          userEmail: userEmail || 'anonymous',
          userName: userName || userEmail?.split('@')[0] || 'Anonymous',
          reason: reason || '',
          comments: feedbackComments || ''
        })
      });
    } catch (error) {
      console.error('Error submitting chatbot feedback:', error);
    } finally {
      setFeedbackSubmitting(false);
      setShowFeedbackPrompt(false);
      setShowReasonStep(false);
      setSelectedReason('');
      setFeedbackComments('');
      setFeedbackTargetText('');
    }
  };

  const handleNotHelpfulClick = () => {
    setShowReasonStep(true);
  };

  const handleReasonSubmit = () => {
    handleFeedbackSubmit('thumbs_down', selectedReason);
  };

  const handleFeedbackClose = () => {
    setShowFeedbackPrompt(false);
    setShowReasonStep(false);
    setSelectedReason('');
    setFeedbackComments('');
    setFeedbackTargetText('');
  };

  const handleFollowUpChoice = (messageId, choice, messageText) => {
    setFollowUpPromptFor('');
    clearFeedbackTimer();

    if (choice === 'yes') {
      setShowFeedbackPrompt(false);
      setFeedbackTargetText('');
      setInputText('Yes, please give me more details about this.');
      textareaRef.current?.focus();
      return;
    }

    setMessages((prev) => ([
      ...prev,
      {
        id: `bot-close-${Date.now()}`,
        text: 'Okay. If you need anything else later, ask me anytime.',
        sender: 'bot',
        timestamp: new Date(),
        isClosingRemark: true,
        replyToMessageId: messageId
      }
    ]));

    setFeedbackTargetText(messageText || '');
    setShowFeedbackPrompt(true);
    textareaRef.current?.focus();
  };

  const handleSendMessage = async () => {
    const trimmedInput = inputText.trim();
    if (!trimmedInput || isLoading) return;

    const requestStartedAt = Date.now();
    const userMessage = {
      id: `user-${requestStartedAt}`,
      text: trimmedInput,
      sender: 'user',
      timestamp: new Date()
    };

    const botMessageId = `bot-${requestStartedAt}`;
    const pendingBotMessage = {
      id: botMessageId,
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      source: 'azure-openai',
      isStreaming: true
    };

    const currentInput = trimmedInput;
    const historySnapshot = buildConversationHistory();
    const sessionStartedAt = messages[0]?.timestamp instanceof Date
      ? messages[0].timestamp.getTime()
      : new Date(messages[0]?.timestamp || Date.now()).getTime();
    const abortController = new AbortController();
    let aiResponseText = '';
    let responseSource = 'azure-openai';

    clearFeedbackTimer();
    setFollowUpPromptFor('');
    setShowFeedbackPrompt(false);
    setFeedbackTargetText('');
    setMessages(prev => [...prev, userMessage, pendingBotMessage]);
    setInputText('');
    setIsLoading(true);
    abortControllerRef.current = abortController;

    try {
      const response = await fetch('/api/chat-ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: abortController.signal,
        body: JSON.stringify({
          conversationId,
          message: currentInput,
          conversationHistory: historySnapshot,
          stream: true,
          userEmail: userEmail || 'anonymous',
          userName: userName || userEmail?.split('@')[0] || 'Anonymous'
        })
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';

        if (contentType.includes('application/json')) {
          const data = await response.json();
          aiResponseText = data.response || "I couldn't generate a response. Please try again.";
          responseSource = data.source || responseSource;
          setFollowUpPromptFor(botMessageId);
          setMessages(prev => prev.map((message) => (
            message.id === botMessageId
              ? { ...message, text: aiResponseText, source: responseSource, isStreaming: false }
              : message
          )));
        } else {
          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) {
            throw new Error('Streaming response is not available in this browser.');
          }

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            if (!chunk) continue;

            aiResponseText += chunk;
            setMessages(prev => prev.map((message) => (
              message.id === botMessageId
                ? { ...message, text: aiResponseText, isStreaming: true, source: responseSource }
                : message
            )));
          }

          aiResponseText = aiResponseText.trim();
          setFollowUpPromptFor(botMessageId);
          setMessages(prev => prev.map((message) => (
            message.id === botMessageId
              ? {
                  ...message,
                  text: aiResponseText || "I couldn't generate a response. Please try again.",
                  isStreaming: false,
                  source: responseSource
                }
              : message
          )));
        }
      } else {
        throw new Error(`API error: ${response.status}`);
      }

      const finalBotMessage = {
        ...pendingBotMessage,
        text: aiResponseText || "I couldn't generate a response. Please try again.",
        source: responseSource,
        isStreaming: false
      };

      const trackedConversation = [...messages, userMessage, finalBotMessage]
        .filter((msg) => !msg.isGreeting)
        .map((msg) => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text,
          timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : new Date(msg.timestamp).toISOString()
        }));

      fetch('/api/track-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId,
          userEmail: userEmail || 'anonymous',
          userName: userName || userEmail?.split('@')[0] || 'Anonymous',
          messages: trackedConversation,
          ticketNumber: 'CHATBOT',
          sessionDuration: Math.floor((Date.now() - sessionStartedAt) / 1000)
        })
      }).catch(err => console.error('Error tracking chatbot interaction:', err));

    } catch (error) {
      if (abortController.signal.aborted || error.name === 'AbortError') {
        const stoppedText = aiResponseText.trim()
          ? `${aiResponseText.trim()}\n\nResponse stopped. You can ask a new question.`
          : 'Response stopped. You can ask a new question.';

        setMessages(prev => prev.map((message) => (
          message.id === botMessageId
            ? {
                ...message,
                text: stoppedText,
                isStreaming: false,
                source: responseSource,
                isStopped: true
              }
            : message
        )));
        return;
      }

      console.error('Error getting AI response:', error);
      const errorMessage = {
        id: botMessageId,
        text: "I'm sorry, I encountered an error while processing your request. Please try again or contact support if the issue persists.",
        sender: 'bot',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => prev.map((message) => (
        message.id === botMessageId ? errorMessage : message
      )));
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatBotText = (text) => {
    if (!text) return text;
    const lines = text.split('\n');
    return lines.map((line, i) => {
      if (/^-{3,}$/.test(line.trim())) {
        return <hr key={i} style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '8px 0' }} />;
      }

      const parseBold = (str) => {
        return str.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });
      };

        // Handle markdown headers
        const h1Match = line.match(/^# (.+)$/);
        if (h1Match) {
          return <h1 key={i} style={{ fontSize: '18px', fontWeight: 700, margin: '12px 0 8px', color: '#0f172a' }}>{parseBold(h1Match[1])}</h1>;
        }

        const h2Match = line.match(/^## (.+)$/);
        if (h2Match) {
          return <h2 key={i} style={{ fontSize: '16px', fontWeight: 700, margin: '10px 0 6px', color: '#1e293b' }}>{parseBold(h2Match[1])}</h2>;
        }

        const h3Match = line.match(/^### (.+)$/);
        if (h3Match) {
          return <h3 key={i} style={{ fontSize: '14px', fontWeight: 700, margin: '8px 0 4px', color: '#334155' }}>{parseBold(h3Match[1])}</h3>;
        }

      if (/^\s*[•\-]\s/.test(line)) {
        const content = line.replace(/^\s*[•\-]\s*/, '');
        return <div key={i} style={{ paddingLeft: '12px', marginBottom: '2px' }}>• {parseBold(content)}</div>;
      }

      const numMatch = line.match(/^\s*(\d+)[.)]\s*(.*)/);
      if (numMatch) {
        return <div key={i} style={{ paddingLeft: '8px', marginBottom: '2px' }}><strong>{numMatch[1]}.</strong> {parseBold(numMatch[2])}</div>;
      }

      return <span key={i}>{parseBold(line)}{i < lines.length - 1 && <br />}</span>;
    });
  };

  return (
    <div className="chatbot-popup">
      <div className="chatbot-header">
        <div className="chatbot-header-content">
          <span className="chatbot-title">🤖 Manual Search Assistant</span>
          <span className="chatbot-status">Online</span>
        </div>
        <div className="chatbot-header-actions">
          <button onClick={handleClearChat} className="chatbot-clear-btn" title="Clear chat history">
            <FaTrash />
          </button>
          <button onClick={handleCloseChat} className="chatbot-close-btn">&times;</button>
        </div>
      </div>

      
      <div className="chatbot-messages">
        {messages.map((message) => (
          <div 
            key={message.id} 
            className={`message ${message.sender === 'user' ? 'message-user' : 'message-bot'}`}
          >
            <div className="message-avatar">
              {message.sender === 'user' ? <FaUser /> : <FaRobot />}
            </div>
            <div className="message-content">
              <div className={`message-text ${message.isError ? 'message-text-error' : ''} ${message.isStreaming ? 'message-text-streaming' : ''}`}>
                {message.sender === 'bot' ? formatBotText(message.text) : message.text}
              </div>
              <div className="message-meta-row">
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                {message.sender === 'bot' && !message.isError && !message.isStreaming && (
                  <button
                    className="message-copy-btn"
                    onClick={() => handleCopyMessage(message.id, message.text)}
                    title="Copy response"
                  >
                    <FaCopy /> {copiedMessageId === message.id ? 'Copied' : 'Copy'}
                  </button>
                )}
              </div>
              {message.sender === 'bot' && !message.isGreeting && !message.isError && !message.isStreaming && followUpPromptFor === message.id && (
                <div className="chatbot-followup-panel">
                  <div className="chatbot-followup-text">Do you want any further information?</div>
                  <div className="chatbot-followup-actions">
                    <button
                      type="button"
                      className="chatbot-followup-btn chatbot-followup-btn-primary"
                      onClick={() => handleFollowUpChoice(message.id, 'yes', message.text)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className="chatbot-followup-btn chatbot-followup-btn-secondary"
                      onClick={() => handleFollowUpChoice(message.id, 'no', message.text)}
                    >
                      No
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && !messages.some((message) => message.isStreaming) && (
          <div className="message message-bot">
            <div className="message-avatar">
              <FaRobot />
            </div>
            <div className="message-content">
              <div className="message-text chatbot-thinking-text">
                <FaSpinner className="spinner" /> Thinking through the best answer...
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <div className="chatbot-input-container">
        <textarea
          ref={textareaRef}
          className="chatbot-input"
          placeholder="Ask ASK AI anything..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          rows="1"
          disabled={isLoading}
        />
        <button 
          className={`chatbot-send-btn ${isLoading ? 'chatbot-stop-btn' : ''}`}
          onClick={isLoading ? handleStopResponse : handleSendMessage}
          disabled={!isLoading && !inputText.trim()}
          title={isLoading ? 'Stop response' : 'Send message'}
        >
          {isLoading ? <FaStop /> : <FaPaperPlane />}
        </button>
      </div>

      {showFeedbackPrompt && (
        <div className="chatbot-feedback-overlay" onClick={handleFeedbackClose}>
          <div className="chatbot-feedback-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="chatbot-feedback-close"
              onClick={handleFeedbackClose}
              aria-label="Close feedback popup"
            >
              <FaTimes />
            </button>

            {!showReasonStep ? (
              <>
                <h3 className="chatbot-feedback-title">Was this response helpful?</h3>
                <p className="chatbot-feedback-subtitle">Your feedback helps improve ASK AI responses.</p>
                <div className="chatbot-feedback-actions">
                  <button
                    type="button"
                    className="chatbot-feedback-btn chatbot-feedback-btn-positive"
                    onClick={() => handleFeedbackSubmit('thumbs_up')}
                    disabled={feedbackSubmitting}
                  >
                    <FaThumbsUp /> Helpful
                  </button>
                  <button
                    type="button"
                    className="chatbot-feedback-btn chatbot-feedback-btn-negative"
                    onClick={handleNotHelpfulClick}
                    disabled={feedbackSubmitting}
                  >
                    <FaThumbsDown /> Not Helpful
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="chatbot-feedback-title">Help us improve</h3>
                <p className="chatbot-feedback-subtitle">What went wrong with this response?</p>
                <div className="chatbot-feedback-reasons">
                  {NEGATIVE_REASONS.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      className={`chatbot-feedback-reason-chip ${selectedReason === reason ? 'chatbot-feedback-reason-chip-selected' : ''}`}
                      onClick={() => setSelectedReason(reason)}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <textarea
                  className="chatbot-feedback-comments"
                  placeholder="Additional comments (optional)..."
                  value={feedbackComments}
                  onChange={(e) => setFeedbackComments(e.target.value)}
                  maxLength={500}
                />
                <div className="chatbot-feedback-char-count">{feedbackComments.length}/500</div>
                <div className="chatbot-feedback-actions chatbot-feedback-actions-reason">
                  <button
                    type="button"
                    className="chatbot-feedback-btn chatbot-feedback-btn-back"
                    onClick={() => { setShowReasonStep(false); setSelectedReason(''); }}
                    disabled={feedbackSubmitting}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="chatbot-feedback-btn chatbot-feedback-btn-submit"
                    onClick={handleReasonSubmit}
                    disabled={!selectedReason || feedbackSubmitting}
                  >
                    {feedbackSubmitting ? <FaSpinner className="spinner" /> : 'Submit'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotPopup;
